import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { TableEnums } from "models/enums";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { ShareholderListProps } from "models/propTypes/holdingRegisterPropTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { CurrencyValueFormatter, TableAsync } from "components/common";
import { useShareRegisterService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, DateHelper } from "utils/helpers";
import clsx from "clsx";

const ShareholderList: React.FC<ShareholderListProps> = ({ snapshotDate }) => {
  const shareRegisterService = useShareRegisterService();
  const filterFunc = React.useCallback(
    (baseFilter: WintUtilsWebApi.Filter) =>
      shareRegisterService.getOwnersAndShares({ snapShotDate: snapshotDate, ...baseFilter }),
    []
  );

  const customSummaryInfoText = (lastConfirmedDateSummary: Date) => {
    return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.LastConfirmed, undefined, {
      date: DateHelper.formatDate(lastConfirmedDateSummary),
    });
  };

  return (
    <Grid item container className={clsx(cssExports.shareholderView, cssExports.whiteTableSummary)}>
      <TableAsync<WintSuperkollAPIModel.ShareOwnerListItem>
        properties={{
          name: {
            description: TranslationKeys.Common.Owner,
            formatter: (shareholder: WintSuperkollAPIModel.ShareOwnerListItem) => shareholder.name,
            noWrap: true,
          },
          identifier: {
            description: TranslationKeys.Common.SSNOrOrgNr,
            formatter: (shareholder: WintSuperkollAPIModel.ShareOwnerListItem) => shareholder.identifier ?? "",
            noWrap: true,
          },
          shareType: {
            description: TranslationKeys.Common.ShareType,
            formatter: (shareholder: WintSuperkollAPIModel.ShareOwnerListItem) => shareholder.shareType,
            noWrap: true,
          },
          quantity: {
            description: TranslationKeys.Common.Quantity,
            formatter: (shareholder: WintSuperkollAPIModel.ShareOwnerListItem) => shareholder.quantity,
            noWrap: true,
            summaryValueFormatter: (values: WintSuperkollAPIModel.ShareOwnerListItem) => values.totalQuantitySummary,
          },
          shareValue: {
            description: TranslationKeys.Common.Value,
            formatter: (shareholder: WintSuperkollAPIModel.ShareOwnerListItem) =>
              new Amount(shareholder.currency, shareholder.shareValue),
            alignment: "right",
            noWrap: true,
            summaryValueFormatter: (values: WintSuperkollAPIModel.ShareOwnerListItem) => (
              <CurrencyValueFormatter cellValue={values.totalValueSummary} />
            ),
          },
        }}
        filter={filterFunc}
        pagination={"advanced"}
        orderByProperty="date"
        orderByDescending
        maxRowCount={10}
        hidePaginationIfSinglePage
        includeSummary
        customSummaryInfo={(shareOwnerListItem: WintSuperkollAPIModel.ShareOwnerListItem) =>
          shareOwnerListItem.lastConfirmedDateSummary !== undefined ? (
            <Grid item xs className={cssExports.summaryText}>
              {customSummaryInfoText(shareOwnerListItem.lastConfirmedDateSummary)}
            </Grid>
          ) : (
            <></>
          )
        }
        customSummaryInfoForExport={(shareOwnerListItem: WintSuperkollAPIModel.ShareOwnerListItem) =>
          shareOwnerListItem.lastConfirmedDateSummary !== undefined
            ? customSummaryInfoText(shareOwnerListItem.lastConfirmedDateSummary)
            : undefined
        }
        tableId={TableEnums.TableType.OwnersList}
      />
    </Grid>
  );
};

export default ShareholderList;
